import SnapLineMode from './SnapLineMode';
import * as Constants from './utils/constants';
import { drawModes, drawState } from '../../Types';
import { cloneTargetSnapshot } from './reshapeEngine';
import { runReshapeCompute } from './reshapeRunner';

/**
 * Reshape mode (ArcGIS Pro "Reshape" / SuperMap "局部更新").
 */
const PartialUpdateMode: drawModes = <drawModes>{ ...SnapLineMode };

const baseOnSetup = (SnapLineMode as any).onSetup;
const baseOnStop = (SnapLineMode as any).onStop;

PartialUpdateMode.onSetup = function (opts: any) {
  const state = baseOnSetup.call(this, opts) as drawState & {
    _targetFeatureId?: string;
    _targetSnapshot?: any;
    _allowSingleIntersection?: boolean;
  };

  const targetId = opts?.featureId != null ? String(opts.featureId) : undefined;
  const target = targetId ? this.getFeature(targetId) : undefined;

  state._targetFeatureId = targetId;
  state._targetSnapshot = target ? cloneTargetSnapshot(target) : undefined;
  state._allowSingleIntersection = opts?.allowSingleIntersection === true;

  return state;
};

PartialUpdateMode.onStop = function (state: any) {
  const drawn = state?.line?.coordinates as number[][] | undefined;
  const targetId = state._targetFeatureId as string | undefined;
  const target = targetId ? this.getFeature(targetId) : undefined;
  const snapshot = state._targetSnapshot;
  const allowSingleIntersection = Boolean(state._allowSingleIntersection);
  const map = this.map;
  const ctx = this;
  const workerUrl = (map as any)?.__geoboxReshapeWorkerUrl as string | undefined;

  try {
    if (state?.line?.id) {
      this.deleteFeature(String(state.line.id), { silent: true });
    }
  } catch {
    /* ignore */
  }

  if (typeof baseOnStop === 'function') {
    baseOnStop.call(this, state);
  }

  const finish = (featureIds: string[] = targetId ? [targetId] : []) => {
    try {
      ctx.changeMode(Constants.modes.SIMPLE_SELECT, { featureIds });
    } catch {
      /* ignore */
    }
    try {
      map?.fire?.('draw.reshape.processing.end');
    } catch {
      /* ignore */
    }
  };

  if (!target || !Array.isArray(drawn) || drawn.length < 2 || !snapshot) {
    try {
      map?.fire?.('draw.reshape.failed', { reason: 'noTarget' });
    } catch {
      /* ignore */
    }
    finish();
    return;
  }

  try {
    map?.fire?.('draw.reshape.processing');
  } catch {
    /* ignore */
  }

  runReshapeCompute(snapshot, drawn, allowSingleIntersection, workerUrl)
    .then((result) => {
      if ('coordinates' in result) {
        try {
          target.incomingCoords(result.coordinates);
        } catch {
          (target as any).coordinates = result.coordinates;
        }
        try {
          map.fire(Constants.events.UPDATE, {
            action: Constants.updateActions.CHANGE_COORDINATES,
            features: [target.toGeoJSON()],
          });
          map.fire('draw.reshape', {
            featureId: targetId,
            action: result.action,
          });
        } catch {
          /* ignore */
        }
      } else {
        try {
          map.fire('draw.reshape.failed', {
            reason: result.reason ?? 'invalidSketch',
          });
        } catch {
          /* ignore */
        }
      }
      finish();
    })
    .catch(() => {
      try {
        map.fire('draw.reshape.failed', { reason: 'invalidSketch' });
      } catch {
        /* ignore */
      }
      finish();
    });
};

export default PartialUpdateMode;
